/**
 * VideoUpload.js (Frontend)
 * 
 * This component handles the UI for the main Video Upload form.
 * It strictly gathers necessary metadata Context (School, Teacher, Grade, Section, Docs) 
 * and handles dispatching the Multipart Form Data to the backend. It also initiates polling
 * to wait for the background AI Analysis process to complete and render the final success modal.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';
import './LessonPlanVideoUpload.css';

// Custom Searchable Dropdown Component
const SearchableSelect = ({ options, value, onChange, placeholder, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%', textAlign: 'left' }}>
            <div 
                onClick={() => !disabled && setIsOpen(!isOpen)}
                style={{
                    width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db', 
                    background: disabled ? '#f3f4f6' : '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    opacity: disabled ? 0.7 : 1, textAlign: 'left'
                }}
            >
                <span style={{ color: value ? '#000' : '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
                    {value || placeholder}
                </span>
                <i className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`} style={{ color: '#6b7280', fontSize: '0.8rem' }}></i>
            </div>

            {isOpen && (
                <div style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: '4px',
                    background: '#fff', border: '1px solid #d1d5db', borderRadius: '8px', 
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '250px', 
                    display: 'flex', flexDirection: 'column'
                }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid #e5e7eb' }}>
                        <input 
                            type="text" 
                            autoFocus
                            placeholder="Type to search..." 
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{ 
                                width: '100%', padding: '0.5rem', borderRadius: '6px', 
                                border: '1px solid #d1d5db', outline: 'none', fontSize: '0.9rem'
                            }}
                        />
                    </div>
                    <div style={{ overflowY: 'auto', flex: 1 }}>
                        {filteredOptions.length === 0 ? (
                            <div style={{ padding: '0.8rem', color: '#6b7280', textAlign: 'center', fontSize: '0.9rem' }}>No matches found</div>
                        ) : (
                            filteredOptions.map(opt => (
                                <div 
                                    key={opt}
                                    onClick={() => {
                                        onChange(opt);
                                        setIsOpen(false);
                                        setSearch('');
                                    }}
                                    style={{
                                        padding: '0.8rem', cursor: 'pointer', borderBottom: '1px solid #f3f4f6',
                                        background: value === opt ? '#f3f4f6' : '#fff', fontSize: '0.9rem',
                                        textAlign: 'left'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = value === opt ? '#f3f4f6' : '#fff'}
                                >
                                    {opt}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const LessonPlanVideoUpload = () => {
    const navigate = useNavigate();
    const [schools, setSchools] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [pdfUrl, setPdfUrl] = useState(null);

    // Rubrics Check State
    const [schoolHasRubrics, setSchoolHasRubrics] = useState(true);
    const [checkingRubrics, setCheckingRubrics] = useState(false);

    // Modal State
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [completedLectureId, setCompletedLectureId] = useState(null);

    // Form Selection State
    const [selectedSchool, setSelectedSchool] = useState('');
    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [lectureNumber, setLectureNumber] = useState(1);
    const [grade, setGrade] = useState('');
    const [section, setSection] = useState('');


    const [lessonPlanFile, setLessonPlanFile] = useState(null);

    // User Role context
    const session = JSON.parse(localStorage.getItem('user')) || {};
    const isSuperAdmin = (session.role || '').toLowerCase().replace(/[\s_]/g, '') === 'superadmin';
    const userSchoolId = session.school_id;

    useEffect(() => {
        if (isSuperAdmin) {
            loadSchools();
        } else if (userSchoolId) {
            setSelectedSchool(userSchoolId);
            loadTeachers(userSchoolId);
            checkSchoolRubrics(userSchoolId);
        }
    }, [isSuperAdmin, userSchoolId]);

    const loadSchools = async () => {
        try {
            const { data } = await api.get('/lesson-plan-schools');
            setSchools(data.data || []);
        } catch (err) {
            console.error("Failed to load schools", err);
        }
    };



    const checkSchoolRubrics = async (schoolId) => {
        try {
            setCheckingRubrics(true);
            const { data } = await api.get(`/lesson-plan-rubrics?school_id=${schoolId}`);
            if (data && data.data && data.data.length === 0) {
                setSchoolHasRubrics(false);
            } else {
                setSchoolHasRubrics(true);
            }
        } catch (err) {
            console.error("Failed to check rubrics", err);
            setSchoolHasRubrics(true); // Default to true on error to avoid false positives
        } finally {
            setCheckingRubrics(false);
        }
    };

    /**
     * Resolves the list of active teachers for a specific school.
     * Required so the admin can assign the uploaded lecture to a specific educator.
     */
    const loadTeachers = async (schoolId) => {
        try {
            // Fetch from lesson plan users routes instead of main users route
            const { data } = await api.get(`/lesson-plan-users/teachers?school_id=${schoolId}&limit=100`);
            setTeachers(data.data || []);
        } catch (err) {
            console.error("Failed to load teachers", err);
            console.error("Error response:", err.response?.data);
        }
    };

    /**
     * Resets dependent dropdowns (like Teachers) when the parent School dropdown changes.
     */
    const handleSchoolChange = (e) => {
        const schoolId = e.target.value;
        setSelectedSchool(schoolId);
        setSelectedTeacher('');
        setSchoolHasRubrics(true);
        if (schoolId) {
            loadTeachers(schoolId);
            checkSchoolRubrics(schoolId);
        } else {
            setTeachers([]);
        }
    };



    // State for Polling
    const [pollingLectureId, setPollingLectureId] = useState(null);

    // Polling Effect
    // Removed old continuous polling hook.
    // The new flow just uploads the video and immediately shows a success message, 
    // letting the user continue their work while AI processes in the background.

    /**
     * Compiles the form context into a multipart FormData payload and fires it off to the backend.
     * Activates the UI loader and sets up the ID required for background polling.
     */
    const handleUpload = async (e) => {
        e.preventDefault();

        if (!selectedSchool || !selectedTeacher || !selectedDate || !grade || !section || !lessonPlanFile) {
            alert("Please complete all steps, including uploading the Lesson Plan PDF.");
            return;
        }

        try {
            setIsLoading(true);
            setUploadProgress(0);
            setMessage('Uploading Lesson Plan... Analysis will start automatically.');
            setPdfUrl(null);

            // Finalize upload parameters
            const finalForm = new FormData();
            finalForm.append('school_id', selectedSchool);
            finalForm.append('teacher_id', selectedTeacher);
            finalForm.append('date', selectedDate);
            finalForm.append('lecture_number', lectureNumber);
            finalForm.append('grade', grade);
            finalForm.append('section', section);

            finalForm.append('lessonPlan', lessonPlanFile);

            setMessage('Sending processing request to Server...');

            const res = await api.post('/lesson-plan-upload', finalForm, {
                timeout: 0,
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });

            setUploadProgress(100);

            if (res.data.status === 'processing' && res.data.lecture_id) {
                // The backend received it and started background analysis
                setIsLoading(false);
                setCompletedLectureId(res.data.lecture_id);
                setShowSuccessModal(true);
            } else {
                // Fallback for immediate path
                if (res.data.pdfReport) {
                    setPdfUrl(res.data.pdfReport);
                }
                setIsLoading(false);
                setShowSuccessModal(true);
            }

        } catch (err) {
            console.error(err);
            setMessage('Upload Failed: ' + (err.response?.data?.message || err.message));
            setIsLoading(false);
            setUploadProgress(0);
        }
    };

    const handleViewReport = () => {
        navigate('/lesson-plan/reports', { state: { lectureId: completedLectureId } });
    };

    const handleCloseModal = () => {
        setShowSuccessModal(false);
        setMessage('Successfully Submitted! You can process another video.');
        // Reset form or other states if needed
    };

    return (
        <div style={{ minHeight: '100vh', background: '#f9fafb' }}>
            {/* Full Screen Loader Overlay */}
            {isLoading && (
                <div className="full-screen-loader">
                    <div className="loader-content">
                        <span className="loader-spinner"></span>
                        <h3 className="loader-title">Uploading Lesson Plan...</h3>
                        <div style={{ width: '100%', marginTop: '1rem', marginBottom: '1rem' }}>
                            <div style={{ background: '#e5e7eb', borderRadius: '8px', overflow: 'hidden', height: '12px' }}>
                                <div style={{ width: `${uploadProgress}%`, background: '#2563eb', height: '100%', transition: 'width 0.3s' }}></div>
                            </div>
                            <p style={{ marginTop: '0.5rem', fontSize: '0.9rem', color: '#4b5563', fontWeight: 'bold' }}>{uploadProgress}% Uploaded</p>
                        </div>
                        <p className="loader-text">
                            Please wait while your files are securely transferred to the server.<br />
                            This may take a minute depending on your internet speed.
                        </p>
                    </div>
                </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
                <div className="modal-overlay">
                    <div className="modal-content" style={{ textAlign: 'center', maxWidth: '500px' }}>
                        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
                        <h2 style={{ fontSize: '1.8rem', fontWeight: 'bold', color: '#111', marginBottom: '1rem' }}>Successfully Submitted!</h2>
                        <p style={{ color: '#4b5563', fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                            Your lesson plan analysis request has been successfully submitted.
                        </p>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            {/* <button
                                onClick={() => navigate('/lesson-plan/reports')}
                                style={{
                                    padding: '0.8rem 1.5rem',
                                    background: '#000',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '1rem'
                                }}
                            >
                                Go to Reports
                            </button> */}
                            <button
                                onClick={handleCloseModal}
                                style={{
                                    padding: '0.8rem 1.5rem',
                                    background: '#f3f4f6',
                                    color: '#374151',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontSize: '1rem'
                                }}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="dashboard-container" style={{ padding: '2rem', filter: isLoading || showSuccessModal ? 'blur(5px)' : 'none', pointerEvents: isLoading || showSuccessModal ? 'none' : 'auto' }}>
                <h2 className="page-title">Upload Lesson Plan</h2>
                <div className="card" style={{ maxWidth: '800px', margin: '2rem auto', padding: '2rem', borderRadius: '16px', background: 'white', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)' }}>

                    <form onSubmit={handleUpload}>
                        {/* ... (Existing Form Content) ... */}
                        {/* Note: I'm preserving the form content structure in the replacement below by mapping it similarly to the original file to avoid accidental deletion of form fields. But since replace_file_content replaces the whole block I select, I must be careful. */}
                        {/* For safety in this specific tool call, I will include the whole component body since I edited the top (hooks) and bottom (modal) */}

                        {/* Step 1: School Selection */}
                        <div className="form-group" style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Select School</label>
                            {isSuperAdmin ? (
                                <select
                                    className="form-control"
                                    value={selectedSchool}
                                    onChange={handleSchoolChange}
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                    required
                                >
                                    <option value="">-- Select School --</option>
                                    {schools.map(s => (
                                        <option key={s.id} value={s.id}>{s.name}</option>
                                    ))}
                                </select>
                            ) : (
                                <input
                                    type="text"
                                    value={userSchoolId ? "My School (Locked)" : "No School Linked"}
                                    disabled
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db', background: '#f3f4f6' }}
                                />
                            )}

                            {!schoolHasRubrics && !checkingRubrics && (
                                <div style={{ marginTop: '0.75rem', padding: '0.75rem', backgroundColor: '#fff3cd', color: '#856404', borderRadius: '8px', fontSize: '0.9rem', border: '1px solid #ffeeba' }}>
                                    <i className="fa-solid fa-circle-exclamation" style={{ marginRight: '8px' }}></i>
                                    Please add your rubrics, otherwise we will use the default rubrics.
                                </div>
                            )}
                        </div>

                        {/* Step 2: Teacher Selection */}
                        <div className="form-group" style={{ marginBottom: '1.5rem', opacity: selectedSchool ? 1 : 0.5 }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Select Teacher</label>
                            <select
                                className="form-control"
                                value={selectedTeacher}
                                onChange={(e) => setSelectedTeacher(e.target.value)}
                                disabled={!selectedSchool}
                                style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                required
                            >
                                <option value="">-- Select Teacher --</option>
                                {teachers.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {t.name} ({t.email})
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Step 3: Date, Lecture Number, Grade & Section */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem', opacity: selectedTeacher ? 1 : 0.5 }}>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Lecture Date</label>
                                <input
                                    type="date"
                                    value={selectedDate}
                                    onChange={(e) => setSelectedDate(e.target.value)}
                                    disabled={!selectedTeacher}
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                    required
                                />
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Lecture Number</label>
                                <select
                                    value={lectureNumber}
                                    onChange={(e) => setLectureNumber(e.target.value)}
                                    disabled={!selectedTeacher}
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                    required
                                >
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                                        <option key={num} value={num}>Lecture {num}</option>
                                    ))}
                                </select>
                            </div>
                            {/* New Grade & Section Fields */}
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Grade / Class</label>
                                <select
                                    value={grade}
                                    onChange={(e) => setGrade(e.target.value)}
                                    disabled={!selectedTeacher}
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db', background: '#fff' }}
                                    required
                                >
                                    <option value="" disabled>-- Select Grade --</option>
                                    <option value="KG1">KG1</option>
                                    <option value="KG2">KG2</option>
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
                                        <option key={num} value={`Grade ${num}`}>Grade {num}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Section</label>
                                <input
                                    type="text"
                                    placeholder="e.g. A, B, Rose"
                                    value={section}
                                    onChange={(e) => setSection(e.target.value)}
                                    disabled={!selectedTeacher}
                                    style={{ width: '100%', padding: '0.8rem', borderRadius: '8px', border: '1px solid #d1d5db' }}
                                    required
                                />
                            </div>
                        </div>




                        {/* Open Sesame (PDF) */}
                        <div className="form-group" style={{ marginBottom: '2rem', opacity: selectedDate ? 1 : 0.5 }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '500' }}>Lesson Plan (.pdf)</label>
                            <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                onChange={(e) => setLessonPlanFile(e.target.files[0])}
                                disabled={!selectedDate}
                                style={{ width: '100%', padding: '0.8rem', border: '1px dashed #d1d5db', borderRadius: '8px' }}
                                required
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={false}
                            style={{
                                width: '100%',
                                padding: '1rem',
                                background: '#000000',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                fontSize: '1rem'
                            }}
                        >
                            <i className="fa-solid fa-cloud-arrow-up" style={{ marginRight: '8px' }}></i>
                            Process AI Analysis
                        </button>
                    </form>

                    {message && !showSuccessModal && (
                        <div style={{ marginTop: '1.5rem', padding: '1rem', background: message.includes('Success') || message.includes('Complete') ? '#ecfdf5' : '#fef2f2', color: message.includes('Success') || message.includes('Complete') ? '#065f46' : '#991b1b', borderRadius: '8px', textAlign: 'center' }}>
                            {message}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LessonPlanVideoUpload;
